import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { pushRegistrationGAEvent } from '@comeon/analytics';
import Cookies from '@comeon/tomcat-cookies';
import { Box } from '@comeon/ui';
import { getUrlParams } from '@comeon/utility';
import { LogoMitID } from '@comeon/vectors';

import { mitidV2Enabled } from '~/components/app-bridge/app-properties';
import ExternalProviders from '~/components/authentication/external-providers';
import ModalPopupLogin from '~/components/authentication/login/modal-popup/modal-popup';
import { VERIFICATION_STEP } from '~/components/authentication/playnow/steps/DGA/constants';
import WizardSidebar from '~/components/authentication/playnow/steps/common/sidebar';
import { selectFranchiseName } from '~/components/brand/ducks/selectors';
import Icon, { ICON_RIGHT, ICON_USER_LINE } from '~/components/icon';
import Loading from '~/components/misc/loading';
import useTranslations from '~/components/translation/use-translations';
import { disableValidateSession } from '~/components/authentication/ducks';
import { useResponse } from '~/components/authentication/playnow/steps/DGA/hooks';

import { END_POINTS, TEXT_KEYS } from './constants';
import {
	StyledButtonContent,
	StyledButtonPrimary,
	StyledButtonWrapper,
	StyledDescription,
	StyledTitle,
	StyledUserIcon
} from './styled';

const Providers = () => {
	const dispatch = useDispatch();

	const mitidCode = getUrlParams('code');
	const mitidError = getUrlParams('error_description');

	const franchiseName = useSelector(selectFranchiseName);
	const useMock = useSelector(state => state.brand.useMockMitid);
	const { translations: t, isLoadingTranslations } = useTranslations(TEXT_KEYS);

	const [provider, updateProvider] = useState(mitidCode || mitidError ? VERIFICATION_STEP.MITID : null);
	const { handleErrors, handleSuccess } = useResponse();

	useEffect(() => {
		if (provider && franchiseName) {
			pushRegistrationGAEvent({
				franchiseName,
				regVerification: provider,
				regScreen: 'verification-selection'
			});
		}
	}, [franchiseName, provider]);

	const initiateMitId = useCallback(() => {
		updateProvider(VERIFICATION_STEP.MITID);
		Cookies.set('dga-verification', VERIFICATION_STEP.MITID);
	}, [updateProvider]);

	const onComplete = useCallback(
		response => {
			const { error, playerId } = response ?? {};

			if (error) {
				handleErrors({ error, provider, playerId });
				return updateProvider(null);
			}

			handleSuccess(response);
		},
		[handleErrors, handleSuccess, provider]
	);

	/**
	 * Auto launch mitid verification
	 */
	useEffect(() => {
		dispatch(disableValidateSession());
		initiateMitId();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const providerProps = useMemo(
		() => ({
			endPoints: END_POINTS,
			meta: { standAlone: !mitidV2Enabled(), useMock },
			onComplete,
			provider
		}),
		[onComplete, provider, useMock]
	);

	if (isLoadingTranslations) {
		return <Loading />;
	}

	return (
		<Box position="absolute" top={0} bottom={0} width="100%">
			<WizardSidebar.Header />
			{provider ? (
				<ExternalProviders {...providerProps} />
			) : (
				<Box
					display="flex"
					justifyContent="center"
					marginTop="5%"
					backgroundColor="var(--sidebar-wrapper-bg)"
					className="mitid-wrapper"
				>
					<Box width={280} p={5} backgroundColor="var(--sidebar-wrapper-bg)">
						<StyledUserIcon>
							<Icon type={ICON_USER_LINE} />
						</StyledUserIcon>
						<StyledTitle>{t.GET_STARTED}</StyledTitle>
						<StyledDescription>{t.VERIFY_POPUP_DESCRIPTION_NEW}</StyledDescription>
						<StyledButtonWrapper>
							<StyledButtonPrimary onClick={initiateMitId} data-at="bin-mitid">
								<StyledButtonContent>
									{t.CONTINUE_WITH}
									<LogoMitID />
									<Icon type={ICON_RIGHT} />
								</StyledButtonContent>
							</StyledButtonPrimary>
						</StyledButtonWrapper>
					</Box>
				</Box>
			)}
			<ModalPopupLogin />
		</Box>
	);
};

export default Providers;
